import React, { useMemo, useState, useEffect } from 'react';
import WorkflowEditor from './components/editors/WorkflowEditor';
import WorkflowSimulator from './components/editors/WorkflowSimulator';
import { EditorProvider } from './contexts/EditorContext';
import { NODE_TYPES_CONFIG, NODE_CATEGORIES } from './constants';
import { PROPERTY_REGISTRY } from './utils/propertyPanelRegistry';
import { defaultComponentRegistry } from './utils/componentRegistry';
import { directoryService } from './services/mockDirectoryService';
import { useWorkflowEditor } from './hooks/useWorkflowEditor';
import { useWorkflowSimulation } from './hooks/useWorkflowSimulation';
import { ProcessDefinition, AIService } from './types';
import { AutoSavePayload } from './hooks/useAutoSave';

export interface ControllerProps {
  initialData?: ProcessDefinition[];
  onBack?: () => void;
  onSave?: (data: AutoSavePayload) => Promise<void>;
  onSaveAndQuit?: (data: AutoSavePayload) => Promise<void>;
  onAutoSave?: (data: AutoSavePayload) => void;
  isLoading?: boolean;
  customComponents?: Record<string, React.ComponentType<any>>;
  aiService?: AIService;
}

const Controller: React.FC<ControllerProps> = ({ 
  initialData, 
  onBack, 
  onSave, 
  onSaveAndQuit, 
  onAutoSave,
  isLoading: externalLoading = false,
  customComponents = {},
  aiService
}) => {
  const [fallbackAi, setFallbackAi] = useState<AIService | null>(null);

  // Lazy-load the default AI service only if a prop one isn't provided.
  // This prevents static imports from triggering buggy SDK top-level initialization.
  useEffect(() => {
    if (!aiService && !fallbackAi) {
      import('./services/geminiService').then(m => {
        setFallbackAi(m.geminiService);
      });
    }
  }, [aiService, fallbackAi]);

  const editor = useWorkflowEditor({
    initialData,
    onAutoSave
  });

  const simulator = useWorkflowSimulation({
      nodes: editor.nodes,
      setNodes: editor.setNodes,
      edges: editor.edges,
      fitView: editor.fitView,
      focusNode: editor.focusNode
  });

  const config = useMemo(() => ({
    nodeTypes: NODE_TYPES_CONFIG,
    nodeCategories: NODE_CATEGORIES,
    propertyRegistry: PROPERTY_REGISTRY,
    componentRegistry: { ...defaultComponentRegistry, ...customComponents },
    services: {
      ai: aiService || fallbackAi || {
          generateWorkflowCompletion: async () => "Initializing AI...",
          evaluateCondition: async () => true
      },
      directory: directoryService
    },
    actions: {
        openModal: editor.openModalAtTab
    }
  }), [customComponents, editor.openModalAtTab, aiService, fallbackAi]);

  const onNodeClick = simulator.simulationState.isRunning ? undefined : editor.onNodeClick;
  const onPaneClick = simulator.simulationState.isRunning ? undefined : editor.onPaneClick;
  const onNodeDoubleClick = simulator.simulationState.isRunning ? undefined : editor.onNodeDoubleClick;
  const onNodeContextMenu = simulator.simulationState.isRunning ? undefined : editor.onNodeContextMenu;

  const handleExternalSave = async () => {
      if (onSave) {
          editor.setIsLoading(true);
          try {
              await onSave(editor.getCurrentProjectSnapshot());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleSaveAndQuitClick = async () => {
      if (onSaveAndQuit) {
          editor.setIsLoading(true);
          try {
              await onSaveAndQuit(editor.getCurrentProjectSnapshot());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleExport = () => {
      editor.handleSaveProject();
  };

  const isGlobalLoading = editor.isLoading || externalLoading;

  return (
    <EditorProvider config={config}>
      {simulator.simulationState.isRunning ? (
        <WorkflowSimulator
          nodes={editor.nodes}
          edges={editor.edges}
          
          simulationState={simulator.simulationState}
          onToggleSimulation={simulator.stopSimulation}
          onResetSimulation={simulator.resetSimulation}
          onSimulationAction={simulator.handleSimulationAction}
          suggestedPath={simulator.suggestedPath}
          
          activeProcessId={editor.activeProcessId}
          processes={editor.processes}
          onSelectProcess={editor.handleSelectProcess}
          onCreateProcess={editor.handleCreateProcess}
          onDeleteProcess={editor.handleDeleteProcess}
          onSaveProject={handleExport}
          onLoadProject={editor.handleLoadProject}
          onBack={onBack}
          onSave={onSave ? handleExternalSave : undefined}
          onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}
        />
      ) : (
        <WorkflowEditor
            nodes={editor.nodes}
            edges={editor.edges}
            onNodesChange={editor.onNodesChange}
            onEdgesChange={editor.onEdgesChange}
            onConnect={editor.onConnect}
            onConnectStart={editor.onConnectStart}
            onConnectEnd={editor.onConnectEnd}
            onDrop={editor.onDrop}
            onDragOver={editor.onDragOver}
            
            simulationState={simulator.simulationState}
            onToggleSimulation={simulator.startSimulation}
            onResetSimulation={simulator.resetSimulation}
            
            activeProcessId={editor.activeProcessId}
            processes={editor.processes}
            onSelectProcess={editor.handleSelectProcess}
            onCreateProcess={editor.handleCreateProcess}
            onDeleteProcess={editor.handleDeleteProcess}
            onUpdateProcess={editor.handleUpdateProcess}
            
            onSaveProject={handleExport}
            onLoadProject={editor.handleLoadProject}
            onAutoLayout={editor.handleAutoLayout}
            
            onBack={onBack}
            onSave={onSave ? handleExternalSave : undefined}
            onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}

            onNodeClick={onNodeClick!}
            onNodeDoubleClick={onNodeDoubleClick!}
            onNodeContextMenu={onNodeContextMenu!}
            onPaneClick={onPaneClick!}
            selectedNode={editor.selectedNode}
            updateNodeData={editor.updateNodeData}
            isModalOpen={editor.isModalOpen}
            setIsModalOpen={editor.setIsModalOpen}
            activeTabId={editor.activeTabId}
            setActiveTabId={editor.setActiveTabId}
            isDeployModalOpen={editor.isDeployModalOpen}
            setIsDeployModalOpen={editor.setIsDeployModalOpen}
            
            isLoading={isGlobalLoading}
            autoSaveState={editor.autoSaveState}
            variableContext={editor.variableContext}
        />
      )}
    </EditorProvider>
  );
};

export default Controller;